import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { getImageUrl } from "helpers/Image.helper";
import { challengeList, challengeStats } from "api/student/v2/challenge.api";
import { IChallenge, IChallengeStats } from "api/interfaces/profile.interface";
import { usePopup } from "contexts/PopupContext";
import ChallengeCreatePopup from "../../popup/ChallengeCreatePopup";

const Challenge = () => {
    const { showPopup } = usePopup();
    const navigate = useNavigate();
    const [challenges, setChallenges] = useState<IChallenge[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState<"all" | "public" | "private" | "my">("all");
    const [stats, setStats] = useState<IChallengeStats | null>(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false
    });

    useEffect(() => {
        loadChallenges();
        loadStats();
    }, [filterType, searchTerm]);

    const loadStats = async () => {
        try {
            const response = await challengeStats();
            setStats(response.stats);
        } catch (error) {
            console.error("챌린지 통계 로드 실패:", error);
        }
    };

    const loadChallenges = async () => {
        try {
            setIsLoading(true);
            
            // API 파라미터 구성
            const params: any = {
                page: pagination.currentPage,
                limit: 10
            };

            // 필터링 파라미터 추가
            if (filterType === "public") {
                params.isPublic = true;
            } else if (filterType === "private") {
                params.isPublic = false;
            } else if (filterType === "my") {
                // 현재 사용자의 챌린지만 조회 (실제로는 현재 사용자 ID 사용)
                // params.userId = currentUserId;
            }

            // 검색 파라미터 추가
            if (searchTerm) {
                params.search = searchTerm;
            }

            // API 호출
            const response = await challengeList(params);
            
            setChallenges(response.challengeList);
            setPagination(response.pagination);
        } catch (error) {
            console.error("챌린지 로드 실패:", error);
            setChallenges([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (filter: "all" | "public" | "private" | "my") => {
        setFilterType(filter);
    };

    const handleCreateChallenge = () => {
        showPopup(
            <ChallengeCreatePopup
                onSuccess={handleCreateSuccess}
            />
        );
    };

    const handleCreateSuccess = (newChallenge: IChallenge) => {
        // 새 챌린지를 목록에 추가
        setChallenges(prev => [newChallenge, ...prev]);
        
        // 통계 새로고침
        loadStats();
        
        // 챌린지 상세 화면으로 이동
        navigate(`/student/growth/challenge/${newChallenge.id}`);
    };

    const handleJoinChallenge = (challengeId: number) => {
        // 챌린지 참여 로직 (추후 구현)
        console.log("챌린지 참여:", challengeId);
    };

    const handleLikeChallenge = (challengeId: number) => {
        // 챌린지 좋아요 로직 (추후 구현)
        console.log("챌린지 좋아요:", challengeId);
    };

    const handleBookmarkChallenge = (challengeId: number) => {
        // 챌린지 북마크 로직 (추후 구현)
        console.log("챌린지 북마크:", challengeId);
    };

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtTitle")}
                </h2>

                {/* 통계 섹션 */}
                <div className="max-w-3xl mx-auto space-y-6 mb-8">
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtStats")}
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-[#CF3688]">
                                        {stats?.totalChallenges || 0}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtTotalChallenges")}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600">
                                        {stats?.publicChallenges || 0}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtPublicChallenges")}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-600">
                                        {stats?.totalParticipants || 0}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtTotalParticipants")}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-purple-600">
                                        {stats?.totalDiaries || 0}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtTotalDiaries")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* 검색 및 필터 섹션 */}
                <div className="max-w-3xl mx-auto space-y-6 mb-8">
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <div className="flex flex-col sm:flex-row gap-4">
                                {/* 검색 입력 */}
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        placeholder={getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtSearchPlaceholder")}
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CF3688] focus:border-transparent"
                                    />
                                </div>
                                
                                {/* 필터 버튼들 */}
                                <div className="flex gap-2">
                                    {[
                                        { key: "all", label: "challenge.txtFilterAll" },
                                        { key: "public", label: "challenge.txtFilterPublic" },
                                        { key: "private", label: "challenge.txtFilterPrivate" },
                                        { key: "my", label: "challenge.txtFilterMy" }
                                    ].map((filter) => (
                                        <button
                                            key={filter.key}
                                            onClick={() => handleFilterChange(filter.key as any)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                filterType === filter.key
                                                    ? "bg-[#CF3688] text-white"
                                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            }`}
                                        >
                                            {getLanguageValue(PAGE_TYPE.STUDENT, filter.label)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* 챌린지 목록 */}
                <div className="max-w-3xl mx-auto space-y-6">
                    {/* 생성 버튼 */}
                    <div className="flex justify-end">
                        <button
                            onClick={handleCreateChallenge}
                            className="bg-[#CF3688] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#B8327A] transition-colors"
                        >
                            {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtCreateChallenge")}
                        </button>
                    </div>

                    {/* 로딩 상태 */}
                    {isLoading && (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#CF3688] mx-auto mb-2"></div>
                            <span className="text-gray-500">로딩 중...</span>
                        </div>
                    )}

                    {/* 챌린지가 없는 경우 */}
                    {!isLoading && challenges.length === 0 && (
                        <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                            <div className="p-6 sm:p-8 text-center">
                                <div className="text-gray-400 mb-4">
                                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtNoChallenges")}
                                </h3>
                                <p className="text-gray-500 mb-6">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtNoChallengesDesc")}
                                </p>
                                <button
                                    onClick={handleCreateChallenge}
                                    className="bg-[#CF3688] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#B8327A] transition-colors"
                                >
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtCreateNewChallenge")}
                                </button>
                            </div>
                        </section>
                    )}

                    {/* 챌린지 목록 */}
                    {!isLoading && challenges.length > 0 && (
                        <div className="space-y-4">
                            {challenges.map((challenge) => (
                                <section key={challenge.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                                    <div className="p-6 sm:p-8">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <Link 
                                                        to={`/student/growth/challenge/${challenge.id}`}
                                                        className="text-xl font-semibold text-gray-900 hover:text-[#CF3688] transition-colors cursor-pointer"
                                                    >
                                                        {challenge.title}
                                                    </Link>
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        challenge.isPublic
                                                            ? "bg-green-100 text-green-800"
                                                            : "bg-gray-100 text-gray-800"
                                                    }`}>
                                                        {challenge.isPublic
                                                            ? getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtPublicChallenge")
                                                            : getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtPrivateChallenge")
                                                        }
                                                    </span>
                                                </div>
                                                
                                                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                        </svg>
                                                        {challenge.user?.userStudent?.nickname || challenge.user?.userKoach?.nickname || 'Unknown'}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {formatTimeAgo(challenge.createdAt)}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-6 text-sm text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        {challenge.diaryCount} {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtDiaryCount")}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                        </svg>
                                                        {challenge.participantCount} {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtParticipantCount")}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 프로필 이미지 */}
                                            {(challenge.user?.userStudent?.profileImage || challenge.user?.userKoach?.profileImage) && (
                                                <div className="ml-4">
                                                    <img
                                                        src={getImageUrl(
                                                            challenge.user?.userStudent?.profileImage || challenge.user?.userKoach?.profileImage, 
                                                            60, 
                                                            60
                                                        )}
                                                        alt={challenge.user?.userStudent?.nickname || challenge.user?.userKoach?.nickname || 'Unknown'}
                                                        className="w-15 h-15 rounded-full object-cover"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* 액션 버튼들 */}
                                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                            <div className="flex items-center gap-4">
                                                <button
                                                    onClick={() => handleJoinChallenge(challenge.id)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B8327A] transition-colors"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                    </svg>
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtJoinChallenge")}
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleLikeChallenge(challenge.id)}
                                                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#CF3688] transition-colors"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                    </svg>
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeLike")}
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleBookmarkChallenge(challenge.id)}
                                                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#CF3688] transition-colors"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                                    </svg>
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeBookmark")}
                                                </button>
                                            </div>

                                            <Link
                                                to={`/student/growth/challenge/${challenge.id}`}
                                                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#CF3688] transition-colors"
                                            >
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeView")}
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </Link>
                                        </div>
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default Challenge;
